import React, {
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import PropTypes from "prop-types";
import localforage from "localforage";
import styles from "./AudioPlayer.module.scss";
import playIcon from "./play.svg";
import pauseIcon from "./pause.svg";
import left10Icon from "./left10.svg";
import right10Icon from "./right10.svg";

// eslint-disable-next-line react/display-name
const AudioPlayer = forwardRef(({ audioSrc }, ref) => {
  const audioRef = useRef(null);
  const progressBarRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isError, setIsError] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const initializeAudio = async () => {
      try {
        await fetchAudio();
      } catch (error) {
        console.error("Error executing fetchAudio:", error);
        setIsError(true);
      }

      const audio = audioRef.current;
      const updateCurrentTime = () => setCurrentTime(audio.currentTime);
      const setAudioDuration = () => setDuration(audio.duration);
      const handleAudioEnd = () => setIsPlaying(false);
      const handleError = () => {
        setIsError(true);
        setIsPlaying(false);
      };

      audio.addEventListener("timeupdate", updateCurrentTime);
      audio.addEventListener("loadedmetadata", setAudioDuration);
      audio.addEventListener("ended", handleAudioEnd);
      audio.addEventListener("error", handleError);

      return () => {
        audio.removeEventListener("timeupdate", updateCurrentTime);
        audio.removeEventListener("loadedmetadata", setAudioDuration);
        audio.removeEventListener("ended", handleAudioEnd);
        audio.removeEventListener("error", handleError);
      };
    };

    initializeAudio();
  }, [audioSrc]);

  const fetchAudio = async () => {
    try {
      const cachedAudio = await localforage.getItem(audioSrc);
      if (cachedAudio) {
        const audioURL = URL.createObjectURL(
          new Blob([cachedAudio], { type: "audio/mpeg" })
        );
        audioRef.current.src = audioURL;
        setIsError(false);
      } else {
        audioRef.current.src = audioSrc;
      }
    } catch (error) {
      console.error("Error loading audio from cache:", error);
      setIsError(true);
    }
  };

  const togglePlay = () => {
    if (isError) return;
    const audio = audioRef.current;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  useImperativeHandle(ref, () => ({
    togglePlay,
    isPlaying,
  }));

  const skipTime = (seconds) => {
    audioRef.current.currentTime += seconds;
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const handleProgressBarClick = (event) => {
    const progressBarWidth = progressBarRef.current.offsetWidth;
    const clickX = event.nativeEvent.offsetX;
    const newTime = (clickX / progressBarWidth) * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handlePointerDown = (event) => {
    setIsDragging(true);
    handleDrag(event);
  };

  const handlePointerMove = (event) => {
    if (isDragging) handleDrag(event);
  };

  const handlePointerUp = () => {
    if (isDragging) setIsDragging(false);
  };

  const handleDrag = (event) => {
    const progressBar = progressBarRef.current;
    const progressBarWidth = progressBar.offsetWidth;
    const offsetX = event.clientX - progressBar.getBoundingClientRect().left;
    const newTime =
      Math.max(0, Math.min(offsetX / progressBarWidth, 1)) * duration;
    audioRef.current.currentTime = newTime; // Move real audio time
    setCurrentTime(newTime);
  };

  useEffect(() => {
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [isDragging]);

  return (
    <div className={styles.audioPlayer}>
      <audio ref={audioRef} preload="metadata" />

      {isError ? (
        <div className={styles.error}>
          Audio source not supported or missing
        </div>
      ) : (
        <>
          <div
            className={styles.progressBarContainer}
            ref={progressBarRef}
            onClick={handleProgressBarClick}
            onPointerDown={handlePointerDown}
          >
            <div
              className={styles.progressBar}
              style={{ width: `${(currentTime / duration) * 100}%` }}
            ></div>
          </div>
          <div className={styles.timeInfo}>
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </>
      )}

      <div className={styles.controls}>
        <img
          src={left10Icon}
          alt="Rewind 10 seconds"
          onClick={() => !isError && skipTime(-10)}
        />
        <img
          src={isPlaying ? pauseIcon : playIcon}
          alt={isPlaying ? "Pause" : "Play"}
          onClick={togglePlay}
        />
        <img
          src={right10Icon}
          alt="Forward 10 seconds"
          onClick={() => !isError && skipTime(10)}
        />
      </div>
    </div>
  );
});

AudioPlayer.propTypes = {
  audioSrc: PropTypes.string.isRequired,
};

export default AudioPlayer;
